import React from "react";
import cn from "classnames";
import { twMerge } from "tailwind-merge";
import classNames from "classnames";
import { Icon } from "../Icon/Icon";
import { HeadlessUi } from "../headlessUi";
import { observer } from "mobx-react-lite";
import { SelectHeader } from "../Select/components/SelectHeader";
import Tag from "../Select/components/Tag";
export const Listbox = observer((props) => {
    const hasError = props.errorText != null;
    return (React.createElement(HeadlessUi.Listbox, { disabled: props.disabled, value: props.selected, multiple: props.multiple, onChange: (option) => {
            if (props.multiple) {
                option = option;
                if (props.onSelect != null) {
                    props.onSelect(option);
                }
                else {
                    const oldIds = new Set(props.selected.map((option) => option.id));
                    const newIds = new Set(option.map((option) => option.id));
                    const added = option.filter((option) => !oldIds.has(option.id));
                    const removed = props.selected.filter((option) => !newIds.has(option.id));
                    const changedOption = added.length > 0 ? added[0] : removed[0];
                    props.onToggle(changedOption.id);
                }
            }
            else {
                option = option;
                props.onSelect(option);
            }
        } }, ({ open }) => (React.createElement(React.Fragment, null,
        React.createElement(HeadlessUi.ListboxButton, { id: props.id, disabled: props.disabled, "data-cy": props.dataCySuffix
                ? `ukt-listbox-${props.dataCySuffix}`
                : "ukt-listbox", className: twMerge("min-h-[48px] w-full py-1 pl-4 pr-[5px]", "rounded-[10px] border-[1px] border-solid border-separator-color", "typography-main bg-input-bg-color text-text-color", "shadow-input_inner", hasError && "border-delete-color", props.className) }, ({ open }) => props.multiple ? (React.createElement(MultiSelectButtonContent, { selected: props.selected, onSelect: props.onSelect, onToggle: props.onToggle, isOpen: open, placeholder: props.placeholder, nonClosableIds: props.nonClosableIds })) : (React.createElement(SingleSelectButtonContent, { selected: props.selected, onSelect: props.onSelect, isOpen: open, placeholder: props.placeholder, renderSelected: props.renderSelected }))),
        hasError && !open && (React.createElement("div", { className: "relative" },
            React.createElement("span", { className: "typography-small absolute left-4 text-delete-color" }, props.errorText))),
        React.createElement(ListboxOptions, { className: props.optionsContainerClassName, options: props.options, headers: props.headers, width: props.optionsWidth, anchor: props.optionsAnchor, dataCySuffix: props.dataCySuffix })))));
});
const SingleSelectButtonContent = observer(({ selected, renderSelected: _renderSelected, placeholder, isOpen, }) => {
    const renderSelected = _renderSelected ??
        (() => {
            return selected != null ? (selected.name) : (React.createElement("span", { className: "text-placeholder-color" }, placeholder ?? "Select"));
        });
    return (React.createElement("div", { className: "flex flex-row items-center gap-1" },
        React.createElement("div", { className: "flex w-full justify-start overflow-hidden whitespace-pre-wrap" }, renderSelected()),
        React.createElement(Icon, { className: classNames("icon input-select-icon transition-all", isOpen && "rotate-180") })));
});
const MultiSelectButtonContent = observer(({ selected, onSelect, onToggle, nonClosableIds, placeholder, isOpen, }) => {
    const toggleSelected = (option) => {
        if (onSelect) {
            const updatedSelected = [...selected];
            const index = updatedSelected.findIndex((element) => element.id === option.id);
            if (index > -1) {
                updatedSelected.splice(index, 1);
            }
            else {
                updatedSelected.push(option);
            }
            onSelect(updatedSelected);
        }
        else if (onToggle) {
            onToggle(option.id);
        }
    };
    const tags = selected.map((option) => {
        const isClosable = nonClosableIds == null || !nonClosableIds.includes(option.id);
        return (React.createElement(Tag, { key: String(option.id), option: option, onClose: isClosable
                ? (event) => {
                    event.stopPropagation();
                    toggleSelected(option);
                }
                : undefined }));
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "flex flex-row flex-wrap items-center gap-y-2" }, tags),
        tags.length === 0 && (React.createElement("div", { className: "flex flex-row items-center gap-1" },
            React.createElement("div", { className: "flex w-full justify-start overflow-hidden whitespace-pre-wrap" },
                React.createElement("span", { className: "text-placeholder-color" }, placeholder ?? "Select")),
            React.createElement(Icon, { className: classNames("icon input-select-icon transition-all", isOpen && "rotate-180") })))));
});
const ListboxOptions = observer(({ className, options, headers, width = "fit-parent", anchor = "bottom start", dataCySuffix, }) => {
    const renderSelectOptions = () => {
        const mapped = options.map((option) => (React.createElement(HeadlessUi.ListboxOption, { key: String(option.id), value: option }, ({ selected, focus }) => (React.createElement(SelectOption, { focused: focus, selected: selected }, option.name)))));
        if (headers) {
            for (let i = 0; i < headers.length; i++) {
                const header = headers[i];
                mapped.splice(i + header.optionIndex, 0, React.createElement(SelectHeader, null, header.name));
            }
        }
        return mapped;
    };
    return (React.createElement(HeadlessUi.ListboxOptions, { "data-cy": dataCySuffix
            ? `ukt-listbox-options-${dataCySuffix}`
            : "ukt-listbox-options", className: twMerge("z-[1000] overflow-clip rounded-[10px] border-[1px] border-solid border-separator-color", "max-h-[200px] w-full bg-on-main-color shadow-big [--anchor-gap:4px]", width === "fit-parent" && "w-[var(--button-width)]", className), anchor: anchor, style: width === "fit-parent"
            ? undefined
            : {
                width: width == "fit-content" ? "auto" : width,
            } }, options.length === 0 ? (React.createElement("div", { className: cn("typography-main text-gray-text-color", "my-[20px] mx-0 text-center") }, "No options")) : (renderSelectOptions())));
});
const SelectOption = ({ children, selected, focused, }) => {
    return (React.createElement("div", { className: cn("typography-main_sb flex cursor-pointer flex-row gap-2 py-[6px] px-4", selected
            ? "bg-[#F6F7F8] text-text-color"
            : "bg-on-main-color text-gray-text-color", focused && "bg-[#F6F7F8] ") },
        React.createElement("div", { className: "max-w-full flex-1 overflow-hidden text-ellipsis" }, children),
        selected && React.createElement(Icon, { className: "check-in-object-icon icon-lg" })));
};
//# sourceMappingURL=Listbox.js.map